import { ArrowUpRight, BookOpen, CalendarClock, GraduationCap, Layers, PauseCircle, Radio } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Magnetic,
  MediaFrame,
  NavyAtmosphere,
  Parallax,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
  TextStagger,
} from '@/components/motion';
import { PROGRAMMES_BY_TYPE, programmeImage } from '@/data/programmes';
import { Badge, Button, Card, CardContent, cn } from '@ibas/ui';

/** Hero video (online MBA promotional clip) + a remote-class photo as its poster. */
const HERO_VIDEO = '/media/videos/online-mba-promotional.mp4';
const HERO_POSTER = '/media/images/people/online-class-video-call.webp';

interface Step {
  title: string;
  description: string;
  Icon: typeof CalendarClock;
}

/**
 * The Flexi-Learn journey. Copy is drawn only from the verified content bank —
 * rolling monthly intakes, the three study modes, pause-anytime, the Flexi-MBA
 * minimum of eight months and the Flexi-DBA applied-doctoral route — nothing
 * invented (no fees, dates or unstated durations).
 */
const STEPS: readonly Step[] = [
  {
    title: 'Rolling monthly intakes',
    description:
      'Modules open on a rolling monthly basis, so you can begin the moment it suits you — there is no fixed start date to wait for.',
    Icon: CalendarClock,
  },
  {
    title: 'Choose your study mode',
    description:
      'Study self-paced, join live online sessions, or blend the two — shape delivery around the way you work best.',
    Icon: Radio,
  },
  {
    title: 'Study, and pause anytime',
    description:
      'Pace the programme around work and life, and pause your studies whenever you need to. Pick up exactly where you left off.',
    Icon: PauseCircle,
  },
  {
    title: 'Graduate on your timeline',
    description:
      'Complete your qualification at your own pace — the Flexi-MBA in a minimum of eight months, the Flexi-DBA as an applied doctoral route.',
    Icon: GraduationCap,
  },
];

interface Mode {
  name: string;
  kind: string;
  description: string;
  Icon: typeof BookOpen;
}

/** The three delivery modes — self-study (asynchronous), live online (synchronous), blended. */
const MODES: readonly Mode[] = [
  {
    name: 'Self-Study',
    kind: 'Asynchronous',
    description:
      'Work through modules entirely on your own schedule, wherever you are — the fullest expression of studying your way.',
    Icon: BookOpen,
  },
  {
    name: 'Live Online',
    kind: 'Synchronous',
    description:
      'Learn in real time through live online sessions, with the structure and interaction of a scheduled class.',
    Icon: Radio,
  },
  {
    name: 'Blended',
    kind: 'Self-study + live',
    description:
      'Combine self-paced study with live online sessions — the balance that fits the rhythm of your week.',
    Icon: Layers,
  },
];

/**
 * OnlineIntroPage (/programmes/online-learning) — an introduction to studying
 * online with IBAS through the Flexi-Learn model. A premium, motion-led
 * explainer: a media-plate hero, the four-step Flexi-Learn journey, the three
 * study modes, the two online programmes (Flexi-MBA & Flexi-DBA), and a
 * closing navy call to action.
 *
 * Motion: SplitReveal for headlines, ScrollReveal (with child stagger) for the
 * step/mode/programme sequences, a scroll-scrubbed Parallax plus pointer-driven
 * PointerParallax on the hero media, and a Magnetic gold CTA on navy. Every
 * primitive is reduced-motion safe and never strands content hidden.
 */
export function OnlineIntroPage() {
  const onlineProgrammes = PROGRAMMES_BY_TYPE.Online;

  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-background py-16 md:py-24">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="max-w-xl">
            <TextStagger
              text="Online learning"
              className="font-label text-xs tracking-[0.24em] text-brand-gold uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.05] text-foreground md:text-6xl"
            >
              Study online, your way.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
                IBAS online programmes follow the <strong className="text-foreground">Flexi-Learn</strong>{' '}
                model. Modules open on a rolling monthly basis, so you can begin when it suits you,
                pace your study around work and life, and pause at any time — fully online, entirely
                on your terms.
              </p>
            </ScrollReveal>
            <ScrollReveal from="up" delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Magnetic strength={0.35} radius={130}>
                  <Button asChild size="lg" variant="primary" data-cursor>
                    <Link to="/apply">Start your application</Link>
                  </Button>
                </Magnetic>
                <Button asChild size="lg" variant="outline">
                  <Link to="/admissions#find-advisor">Connect with an adviser</Link>
                </Button>
              </div>
            </ScrollReveal>
          </div>

          {/* Media plate — scroll parallax + pointer parallax for real depth. */}
          <Parallax speed={0.12} className="mx-auto w-full max-w-md lg:max-w-none">
            <PointerParallax strength={18}>
              <div data-parallax data-depth="0.7">
                <MediaFrame
                  image={HERO_POSTER}
                  video={HERO_VIDEO}
                  alt="IBAS students watching a remote instructor via video call during an online learning session."
                  aspect="aspect-[4/5]"
                  overlay={45}
                  rounded="rounded-2xl"
                  position="50% 40%"
                >
                  <div className="absolute inset-x-6 bottom-6 md:inset-x-8 md:bottom-8">
                    <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
                      Flexi-Learn
                    </span>
                    <p className="mt-1 font-display text-2xl leading-tight text-white md:text-3xl">
                      Learn anywhere, any time.
                    </p>
                  </div>
                </MediaFrame>
              </div>
            </PointerParallax>
          </Parallax>
        </div>
      </section>

      {/* ── HOW FLEXI-LEARN WORKS ─────────────────────────────────────────── */}
      <section className="border-t border-border bg-muted/30 py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <header className="max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              How Flexi-Learn works
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              Four steps, entirely on your schedule.
            </SplitReveal>
          </header>

          <ScrollReveal from="left" className="mt-8 max-w-3xl">
            <blockquote className="border-l-2 border-brand-gold pl-5 font-display text-xl leading-snug text-foreground/90 italic md:text-2xl">
              “At IBAS, our vision is to make education affordable and accessible so you can learn
              anywhere, any time, and in any learning method that matches your personality.”
            </blockquote>
          </ScrollReveal>

          <ScrollReveal
            stagger={0.1}
            className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {STEPS.map((step, index) => (
              <div
                key={step.title}
                className="relative flex h-full flex-col rounded-xl border border-border bg-background p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-3xl font-semibold text-brand-gold">
                    {`0${index + 1}`}
                  </span>
                  <step.Icon aria-hidden className="size-6 text-muted-foreground" />
                </div>
                <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {step.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── STUDY MODES ───────────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <header className="max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Study modes
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              Choose how you learn.
            </SplitReveal>
            <p className="mt-4 text-base text-muted-foreground md:text-lg">
              Every online programme can be studied in the mode that matches your life — and you can
              move between them as your circumstances change.
            </p>
          </header>

          <ScrollReveal stagger={0.1} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {MODES.map((mode) => (
              <Card
                key={mode.name}
                className="h-full gap-0 border-border py-0 transition-shadow duration-500 hover:shadow-xl motion-reduce:transition-none"
              >
                <CardContent className="flex h-full flex-col p-6">
                  <div className="flex size-12 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                    <mode.Icon aria-hidden className="size-6" />
                  </div>
                  <Badge variant="secondary" className="mt-5 w-fit font-label tracking-wider uppercase">
                    {mode.kind}
                  </Badge>
                  <h3 className="mt-3 font-display text-2xl leading-tight font-semibold text-foreground">
                    {mode.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {mode.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── THE TWO ONLINE PROGRAMMES ─────────────────────────────────────── */}
      <section className="border-t border-border bg-muted/30 py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <header className="max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              The online programmes
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              One programme. Two qualifications. Broader leverage.
            </SplitReveal>
            <p className="mt-4 text-base text-muted-foreground md:text-lg">
              Two fully online routes, both built on the Flexi-Learn model — choose the level that
              matches where you are heading.
            </p>
          </header>

          <ScrollReveal stagger={0.12} className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
            {onlineProgrammes.map((programme) => (
              <Link
                key={programme.slug}
                to={`/programmes/${programme.slug}`}
                data-cursor
                className={cn(
                  'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm',
                  'transition-all duration-500 hover:shadow-2xl motion-reduce:transition-none',
                )}
              >
                <MediaFrame
                  image={programmeImage(programme)}
                  alt={programme.alt}
                  position={programme.imagePosition}
                  aspect="aspect-[16/9]"
                  rounded="rounded-none"
                  reveal={false}
                  overlay={35}
                >
                  <span className="absolute top-5 left-5 font-label text-xs tracking-[0.18em] text-brand-gold-light uppercase">
                    {programme.code}
                  </span>
                </MediaFrame>

                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-2xl leading-tight font-semibold text-foreground md:text-3xl">
                      {programme.title}
                    </h3>
                    <ArrowUpRight
                      aria-hidden
                      className="size-6 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-gold motion-reduce:transition-none"
                    />
                  </div>
                  <p className="mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
                    {programme.summary}
                  </p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {programme.facts.slice(0, 3).map((fact) => (
                      <Badge
                        key={fact.label}
                        variant="outline"
                        className="border-border font-label tracking-wide"
                      >
                        {fact.value}
                      </Badge>
                    ))}
                  </div>

                  {programme.acbsp && programme.retention2025 && (
                    <p className="mt-5 text-xs text-muted-foreground">
                      Student retention 2025 (ACBSP):{' '}
                      <span className="font-semibold text-foreground">{programme.retention2025}</span>
                    </p>
                  )}

                  <span className="mt-6 inline-flex items-center gap-1.5 font-label text-sm tracking-wide text-brand-navy underline decoration-brand-gold/50 underline-offset-4 group-hover:decoration-brand-gold">
                    Explore the {programme.code}
                  </span>
                </div>
              </Link>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── CLOSING NAVY CALL TO ACTION ───────────────────────────────────── */}
      <section className="relative isolate flex min-h-[70vh] items-center overflow-hidden bg-brand-navy">
        <NavyAtmosphere />

        <ScrollReveal className="relative z-10 mx-auto flex max-w-3xl flex-col items-center gap-6 px-6 py-20 text-center">
          <p className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
            Study Your Way. Connect Globally.
          </p>
          <SplitReveal
            as="h2"
            by="words"
            className="font-display text-3xl font-semibold text-white sm:text-5xl"
          >
            Begin when it suits you.
          </SplitReveal>
          <p className="max-w-xl text-balance text-white/70">
            Start on a rolling monthly intake, study in the mode that fits your life, and pause
            whenever you need to. Our advisers can help you choose your route.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Magnetic strength={0.4} radius={140}>
              <Button asChild size="lg" variant="secondary" data-cursor>
                <Link to="/apply">Start your application</Link>
              </Button>
            </Magnetic>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/admissions#find-advisor">Connect with an adviser</Link>
            </Button>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
